import { FC, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { FetchState } from '../../../store/fetchState';
import { getOptionsActivity, selectOptionsActivity, selectOptionsActivityState } from '../../../store/entities/optionsActivity';
import { QcsAutocomplete } from './QcsAutocomplete';
import { CancelToken } from 'axios';
import { getLangNameObj } from '../../../utils/format';
import { useTranslation } from 'react-i18next';
import { QcsTypography } from '../basic/QcsTypography';
import { ActivityDto } from '@api/models/activity/ActivityDto';
import { CertificateDto } from '@api/models/suppliers/CertificateDto';

interface Props {
  multiple?: boolean;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  oneTimeEntry?: boolean;
  excludeIds?: string[];
  showCertificates?: boolean;
  hideWithCertificates?: boolean;
  hideWithRisks?: boolean;
}

export const ActivityAutocomplete: FC<Props> = ({
  multiple,
  name,
  label,
  disabled,
  required,
  oneTimeEntry,
  excludeIds,
  showCertificates,
  hideWithCertificates,
  hideWithRisks,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const optionsActivity = useAppSelector(selectOptionsActivity);
  const optionsActivityState = useAppSelector(selectOptionsActivityState);
  const [selectedActivities, setSelectedActivities] = useState<ActivityDto[]>([]);

  const loadOptions = (
    search: string | undefined,
    cancelToken: CancelToken
  ) => {
    dispatch(getOptionsActivity(cancelToken, oneTimeEntry, search));
  };

  function getOptionName(option: ActivityDto) {
    const optionName = getLangNameObj(i18n, option);

    if (option.certificates?.length === 0 || !showCertificates) {
      return optionName;
    }
    return (
      optionName +
      ' | ' +
      option.certificates?.map((it) => getLangNameObj(i18n, it)).join(', ')
    );
  }

  const certificates: CertificateDto[] = useMemo<CertificateDto[]>(() => {
    const allCertificates = selectedActivities.flatMap(
      (a) => a.certificates ?? []
    );
    return Array.from(
      allCertificates
        .reduce(
          (map, cert) => map.set(cert.id!, cert),
          new Map<string, CertificateDto>()
        )
        .values()
    );
  }, [selectedActivities]);

  return (
    <>
      <QcsAutocomplete
        multiple={multiple}
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        options={optionsActivity.filter((x) => {
          if (excludeIds?.includes(x.id!)) return false;
          if (hideWithCertificates && x.certificates && x.certificates.length > 0) return false;
          if (hideWithRisks && x.riskRequired) return false;
          return true;
        })}
        loading={optionsActivityState === FetchState.Loading}
        loadingError={optionsActivityState === FetchState.Error}
        loadOptions={loadOptions}
        getOptionId={(option: ActivityDto) => option.id!}
        getOptionName={(option: ActivityDto) => getOptionName(option)}
        onChange={(value) => {
          if (!value) value = [];
          setSelectedActivities(Array.isArray(value) ? value : [value]);
        }}
      />

      {certificates.length > 0 && showCertificates && (
        <>
          <QcsTypography>{t('activity.requiredCertificates')}: </QcsTypography>
          <ul style={{ paddingLeft: '2rem' }}>
            {certificates.map((it) => (
              <li key={it.id}>{getLangNameObj(i18n, it)}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};
